import "@/index.css"
import * as Sentry from "@sentry/solid"
import { I18nProvider } from "@openanalyst-ai/ui/context"
import { DialogProvider } from "@openanalyst-ai/ui/context/dialog"
import { FileComponentProvider } from "@openanalyst-ai/ui/context/file"
import { MarkedProvider } from "@openanalyst-ai/ui/context/marked"
import { File } from "@openanalyst-ai/ui/file"
import { Font } from "@openanalyst-ai/ui/font"
import { Splash } from "@openanalyst-ai/ui/logo"
import { ThemeProvider } from "@openanalyst-ai/ui/theme/context"
import { MetaProvider } from "@solidjs/meta"
import { type BaseRouterProps, Navigate, Route, Router, useLocation, useNavigate } from "@solidjs/router"
import { createQuery, QueryClient, QueryClientProvider } from "@tanstack/solid-query"
import { Effect } from "effect"
import {
  type Component,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  ErrorBoundary,
  For,
  type JSX,
  lazy,
  onCleanup,
  onMount,
  type ParentProps,
  Show,
  Suspense,
} from "solid-js"
import { Dynamic } from "solid-js/web"
import { AppLoading } from "@/components/app-loading"
import { NetworkStatusBanner } from "@/components/network-status-banner"
import { clearReturnTo, getReturnTo } from "@/context/auth"
import { authConnectionLoadingMessage } from "@/context/auth/connection-issue"
import { AuthGuard, GuestGuard } from "@/context/auth/guard"
import { AuthSignInAlias } from "@/context/auth/sign-in-alias"
import { useAuth } from "@/context/auth"
import { isAuthRequiredError } from "@/context/auth/fetch"
import { CommandProvider } from "@/context/command"
import { CommentsProvider } from "@/context/comments"
import { FileProvider } from "@/context/file"
import { GlobalProvider } from "@/context/global"
import { GlobalSDKProvider } from "@/context/global-sdk"
import { GlobalSyncProvider } from "@/context/global-sync"
import { HighlightsProvider } from "@/context/highlights"
import { LanguageProvider, type Locale, useLanguage } from "@/context/language"
import { LayoutProvider } from "@/context/layout"
import { ModelsProvider } from "@/context/models"
import { NotificationProvider } from "@/context/notification"
import { PermissionProvider } from "@/context/permission"
import { PromptProvider } from "@/context/prompt"
import { ServerConnection, ServerProvider, serverName, useServer } from "@/context/server"
import { ServerSDKProvider } from "@/context/server-sdk"
import { SettingsProvider } from "@/context/settings"
import { TerminalProvider } from "@/context/terminal"
import DirectoryLayout from "@/pages/directory-layout"
import Layout from "@/pages/layout"
import { ErrorPage } from "./pages/error"
import { isHostedOpenAnalystAppHost } from "./services/openanalyst-hosted-runtime"
import { onboardingBootstrapQueryOptions, resolveOnboardingNavigation } from "./services/onboarding"
import { resolveAuthStarterHandoffPath } from "./services/auth-starter-handoff"
import { isOnboardingV2Completed, isOnboardingV2Enabled, markOnboardingV2Completed } from "./services/onboarding-v2"
import { readPluginRestartIntent } from "./services/plugins"
import { useCheckServerHealth } from "./utils/server-health"

const HomeRoute = lazy(() => import("@/pages/home"))
const loginPageImport = import("@/pages/login")
const LoginPage = lazy(() => loginPageImport)
const VerifyEmailPendingPage = lazy(() => import("@/pages/verify-email-pending"))
const VerifyEmailPage = lazy(() => import("@/pages/verify-email"))
const CallbackPage = lazy(() => import("@/pages/callback"))
const loadSession = () => import("@/pages/session")
const Session = lazy(loadSession)
const MiniChatPage = lazy(() => import("@/pages/mini-chat"))
const SmartworkPage = lazy(() => import("@/pages/smartwork"))
const OpenAnalystRuntimePage = lazy(() => import("@/pages/openanalyst-runtime"))
const OnboardingPage = lazy(() => import("@/pages/onboarding"))
const OnboardingV2Page = lazy(() => import("@/pages/onboarding/v2"))
const StarterHandoffPage = lazy(() => import("@/pages/starter-handoff"))
const OpenAnalystDesktopOnboardingPage = lazy(() => import("@/pages/openanalyst-desktop-onboarding"))
const CloudCrmSelectorPage = lazy(() => import("@/pages/cloud-crm").then((module) => ({ default: module.CloudCrmSelectorPage })))
const CloudCrmProjectResolverPage = lazy(() => import("@/pages/cloud-crm").then((module) => ({ default: module.CloudCrmProjectResolverPage })))
const CloudCrmWorkspacePage = lazy(() => import("@/pages/cloud-crm").then((module) => ({ default: module.CloudCrmWorkspacePage })))
const Loading = () => <AppLoading />

/**
 * New-install / first-session trigger for onboarding v2.
 *
 * Wraps the / and /login routes. When `onboardingV2` flag is on AND this
 * browser has not yet recorded a completion marker, redirects the user to
 * /onboarding/v2/welcome instead of showing the home page or login form.
 * Returning / already-onboarded users pass straight through.
 *
 * Reads localStorage directly via helpers in services/onboarding-v2 so we
 * don't need a SettingsProvider in pre-auth scope.
 */
function OnboardingV2Redirect(props: ParentProps) {
  const auth = useAuth()
  const location = useLocation()
  const shouldRedirect = createMemo(() => {
    if (!isOnboardingV2Enabled()) return false
    // Never intercept an AuthIntent deep-link (/login?authIntentId=…): it must
    // reach the login OTP flow, or the campaign/download handoff context is lost
    // and the user drops into generic onboarding instead of their starter.
    if (location.query.authIntentId) return false
    // Wait for auth init: the desktop-session restore that runs during it is
    // what identifies a RETURNING user (and sets the install-level onboarding
    // marker). Redirecting before it finishes would bounce returning users
    // whose localStorage was cleared into first-run onboarding.
    if (!auth.state().initialized) return false
    if (isOnboardingV2Completed(auth.state().session?.userId ?? null)) return false
    // Authenticated users are governed by OnboardingGate, which checks the
    // server's real onboarding status before deciding. Redirecting here would
    // bounce an already-onboarded returning user back to the welcome screen.
    // Only force first-run onboarding for users who have NOT authenticated on
    // this install; a signed-out returning user falls through to /login.
    if (auth.state().authenticated) return false
    return true
  })
  return (
    <Show when={!shouldRedirect()} fallback={<Navigate href="/onboarding/v2/welcome" />}>
      {props.children}
    </Show>
  )
}

// V2 onboarding is a single canvas now. It starts unauthenticated so the
// user can enter email/OTP in-chat, then reuses the normal provider chain
// as soon as the OpenAnalyst token exists for agent research, starter
// project creation, integrations, and the workspace handoff.
function OnboardingV2RouteWrapper(props: {
  defaultServer: ServerConnection.Key
  servers?: Array<ServerConnection.Any>
  disableHealthCheck?: boolean
}) {
  return (
    <ProtectedLayout
      defaultServer={props.defaultServer}
      servers={props.servers}
      disableHealthCheck={props.disableHealthCheck}
      bare
      allowUnauthenticated
      allowPendingDesktopOnboarding
      allowPendingPasswordless
    >
      <OnboardingV2Page />
    </ProtectedLayout>
  )
}

if (typeof location === "object" && /\/session(?:\/|$)/.test(location.pathname)) {
  void loadSession()
}

const SessionRoute = () => (
  <SessionProviders>
    <Session />
  </SessionProviders>
)

const MiniChatRoute = () => (
  <SessionProviders>
    <MiniChatPage />
  </SessionProviders>
)

const SessionIndexRoute = () => <Navigate href="session" />

function UiI18nBridge(props: ParentProps) {
  const language = useLanguage()
  return <I18nProvider value={{ locale: language.intl, t: language.t }}>{props.children}</I18nProvider>
}

declare global {
  interface Window {
    __OPENANALYST__?: {
      updaterEnabled?: boolean
      deepLinks?: string[]
      wsl?: boolean
      productName?: string
      surfaceLabel?: string
      identifier?: string
    }
    api?: {
      setTitlebar?: (theme: { mode: "light" | "dark" }) => Promise<void>
      compressVideo?: (input: { bytes: ArrayBuffer; filename: string; targetBytes: number }) => Promise<
        | { ok: true; bytes: ArrayBuffer; mime: string }
        | { ok: false; reason: "unavailable" | "failed" }
        | { ok: false; reason: "still-too-large"; compressedBytes: number }
      >
    }
  }
}

function QueryProvider(props: ParentProps) {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
    },
  })
  return <QueryClientProvider client={client}>{props.children}</QueryClientProvider>
}

function AppShellProviders(props: ParentProps<{ bare?: boolean }>) {
  return (
    <SettingsProvider>
      <PermissionProvider>
        <LayoutProvider>
          <NotificationProvider>
            <ModelsProvider>
              <CommandProvider>
                <HighlightsProvider>
                  <NetworkStatusBanner />
                  <Show when={!props.bare} fallback={<>{props.children}</>}>
                    <Layout>{props.children}</Layout>
                  </Show>
                </HighlightsProvider>
              </CommandProvider>
            </ModelsProvider>
          </NotificationProvider>
        </LayoutProvider>
      </PermissionProvider>
    </SettingsProvider>
  )
}

function SessionProviders(props: ParentProps) {
  return (
    <TerminalProvider>
      <FileProvider>
        <PromptProvider>
          <CommentsProvider>{props.children}</CommentsProvider>
        </PromptProvider>
      </FileProvider>
    </TerminalProvider>
  )
}

function AuthRequiredFallback(props: { reset: () => void }) {
  const auth = useAuth()
  // A dropped connection can surface here as an "auth required" error: a token refresh that could
  // not reach the network returns no token, so a request throws AuthRequiredError. That is NOT a
  // real sign-out. If we're offline, keep the session, show "reconnecting", and re-render only once
  // the link returns (the failing request then simply succeeds) — never log the user out for a blip.
  const offline = () => auth.isOffline() || (typeof navigator !== "undefined" && navigator.onLine === false)
  onMount(() => {
    if (offline()) {
      const onOnline = () => props.reset()
      window.addEventListener("online", onOnline, { once: true })
      onCleanup(() => window.removeEventListener("online", onOnline))
      return
    }
    void auth.logout().catch(() => {}).finally(props.reset)
  })
  return (
    <AppLoading
      message={
        offline()
          ? authConnectionLoadingMessage(auth.connectionIssue?.() ?? auth.state().connectionIssue)
          : "Signing in again..."
      }
    />
  )
}

function shouldMigrateLegacyHostedThemeDefault() {
  if (import.meta.env.VITE_OPENANALYST_HOSTED_RUNTIME === "1") return true
  if (typeof location !== "object") return false
  return isHostedOpenAnalystAppHost(location.hostname)
}

export function AppBaseProviders(props: ParentProps<{ locale?: Locale }>) {
  return (
    <MetaProvider>
      <Font />
      <ThemeProvider
        migrateLegacyHostedDefaultTheme={shouldMigrateLegacyHostedThemeDefault()}
        onThemeApplied={(_, mode) => {
          void window.api?.setTitlebar?.({ mode })
        }}
      >
        <LanguageProvider locale={props.locale}>
          <UiI18nBridge>
            <ErrorBoundary
              fallback={(error, reset) => {
                if (isAuthRequiredError(error)) return <AuthRequiredFallback reset={reset} />
                Sentry.captureException(error)
                return <ErrorPage error={error} />
              }}
            >
              <QueryProvider>
                <DialogProvider>
                  <MarkedProvider>
                    <FileComponentProvider component={File}>{props.children}</FileComponentProvider>
                  </MarkedProvider>
                </DialogProvider>
              </QueryProvider>
            </ErrorBoundary>
          </UiI18nBridge>
        </LanguageProvider>
      </ThemeProvider>
    </MetaProvider>
  )
}

function ConnectionGate(props: ParentProps<{ disableHealthCheck?: boolean }>) {
  if (props.disableHealthCheck) return <>{props.children}</>

  const server = useServer()
  const checkServerHealth = useCheckServerHealth()

  const [checkMode, setCheckMode] = createSignal<"blocking" | "background">("blocking")

  // performs repeated health check with a grace period for
  // non-http connections, otherwise fails instantly
  const [startupHealthCheck, healthCheckActions] = createResource(() =>
    props.disableHealthCheck
      ? true
      : Effect.gen(function* () {
          if (!server.current) return true
          const { http, type } = server.current

          while (true) {
            const res = yield* Effect.promise(() => checkServerHealth(http))
            if (res.healthy) return true
            if (checkMode() === "background" || type === "http") return false
          }
        }).pipe(
          Effect.timeoutOrElse({ duration: "10 seconds", orElse: () => Effect.succeed(false) }),
          Effect.ensuring(Effect.sync(() => setCheckMode("background"))),
          Effect.runPromise,
        ),
  )

  const healthCheckReady = createMemo(() =>
    checkMode() === "blocking" ? !startupHealthCheck.loading : startupHealthCheck.state !== "pending",
  )
  const startupHealthy = createMemo(() => startupHealthCheck.latest === true)

  return (
    <Show
      when={healthCheckReady()}
      fallback={
        <AppLoading message="Starting local OpenAnalyst server..." />
      }
    >
      <Show
        when={startupHealthy()}
        fallback={
          <ConnectionError
            onRetry={() => {
              if (checkMode() === "background") void healthCheckActions.refetch()
            }}
            onServerSelected={(key) => {
              setCheckMode("blocking")
              server.setActive(key)
              void healthCheckActions.refetch()
            }}
          />
        }
      >
        {props.children}
      </Show>
    </Show>
  )
}

function ConnectionError(props: { onRetry?: () => void; onServerSelected?: (key: ServerConnection.Key) => void }) {
  const language = useLanguage()
  const server = useServer()
  const others = () => server.list.filter((s) => ServerConnection.key(s) !== server.key)
  const name = createMemo(() => server.name || server.key)
  const serverToken = "\u0000server\u0000"
  const unreachable = createMemo(() => language.t("app.server.unreachable", { server: serverToken }).split(serverToken))

  const timer = setInterval(() => props.onRetry?.(), 1000)
  onCleanup(() => clearInterval(timer))

  return (
    <div class="h-dvh w-screen flex flex-col items-center justify-center bg-background-base gap-6 p-6">
      <div class="flex flex-col items-center max-w-md text-center">
        <Splash class="w-12 h-15 mb-4" />
        <p class="text-14-regular text-text-base">
          {unreachable()[0]}
          <span class="text-text-strong font-medium">{name()}</span>
          {unreachable()[1]}
        </p>
        <p class="mt-1 text-12-regular text-text-weak">{language.t("app.server.retrying")}</p>
      </div>
      <Show when={others().length > 0}>
        <div class="flex flex-col gap-2 w-full max-w-sm">
          <span class="text-12-regular text-text-base text-center">{language.t("app.server.otherServers")}</span>
          <div class="flex flex-col gap-1 bg-surface-base rounded-lg p-2">
            <For each={others()}>
              {(conn) => {
                const key = ServerConnection.key(conn)
                return (
                  <button
                    type="button"
                    class="flex items-center gap-3 w-full px-3 py-2 rounded-md hover:bg-surface-raised-base-hover transition-colors text-left"
                    onClick={() => props.onServerSelected?.(key)}
                  >
                    <span class="text-14-regular text-text-strong truncate">{serverName(conn)}</span>
                  </button>
                )
              }}
            </For>
          </div>
        </div>
      </Show>
    </div>
  )
}

function ServerKey(props: ParentProps) {
  const server = useServer()
  return (
    <Show when={server.key} keyed>
      {props.children}
    </Show>
  )
}

function ProtectedLayout(
  props: ParentProps<{
    defaultServer: ServerConnection.Key
    servers?: Array<ServerConnection.Any>
    disableHealthCheck?: boolean
    appChildren?: JSX.Element
    // When true, skip the workspace chrome (<Layout>) and the OnboardingGate
    // redirect logic — give just the auth + provider chain. Used by v3
    // onboarding so its post-auth screens get the SDK / Settings / Layout
    // contexts in scope without rendering inside the workspace shell.
    bare?: boolean
    allowPendingDesktopOnboarding?: boolean
    allowPendingPasswordless?: boolean
    allowUnauthenticated?: boolean
  }>,
) {
  return (
    <AuthGuard
      allowPendingDesktopOnboarding={props.allowPendingDesktopOnboarding}
      allowPendingPasswordless={props.allowPendingPasswordless}
      allowUnauthenticated={props.allowUnauthenticated}
    >
      <ServerProvider
        defaultServer={props.defaultServer}
        disableHealthCheck={props.disableHealthCheck}
        servers={props.servers}
      >
        <ConnectionGate disableHealthCheck={props.disableHealthCheck}>
          <ServerKey>
            <GlobalSDKProvider>
              <GlobalProvider>
                <ServerSDKProvider>
                  <GlobalSyncProvider>
                    <AppShellProviders bare={props.bare}>
                      <Show
                        when={!props.bare}
                        fallback={
                          <>
                            {props.appChildren}
                            {props.children}
                          </>
                        }
                      >
                        <OnboardingGate>
                          {props.appChildren}
                          {props.children}
                        </OnboardingGate>
                      </Show>
                    </AppShellProviders>
                  </GlobalSyncProvider>
                </ServerSDKProvider>
              </GlobalProvider>
            </GlobalSDKProvider>
          </ServerKey>
        </ConnectionGate>
      </ServerProvider>
    </AuthGuard>
  )
}

function OnboardingGate(props: ParentProps) {
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const bootstrap = createQuery(() => ({
    ...onboardingBootstrapQueryOptions(auth.state().session?.userId),
    enabled: auth.state().authenticated,
  }))

  createEffect(() => {
    // V2 onboarding redirect — takes priority over the v1 bootstrap-pending
    // logic below. Fires anywhere inside ProtectedLayout (including the
    // /:dir/* project workspace routes) so reopening the app on a project
    // still routes a new/incomplete user through onboarding.
    const userId = auth.state().session?.userId ?? null
    if (
      isOnboardingV2Enabled() &&
      !isOnboardingV2Completed(userId) &&
      !location.pathname.startsWith("/onboarding/v2")
    ) {
      // Decide off the server's real onboarding status, not just the local
      // marker. Wait for bootstrap so a returning user who is already onboarded
      // server-side is not bounced back to the welcome screen.
      if (bootstrap.isPending || bootstrap.isError || !bootstrap.data) return
      if (bootstrap.data.pending) {
        navigate("/onboarding/v2/welcome", { replace: true })
        return
      }
      // Authenticated and onboarding already complete server-side: record it so
      // future opens (and the pre-auth redirect) skip onboarding, then stay put.
      markOnboardingV2Completed(userId)
    }

    if (bootstrap.isPending || bootstrap.isError || !bootstrap.data) return
    if (
      bootstrap.data.pending &&
      location.pathname !== "/onboarding" &&
      // Don't bounce v3 onboarding (which lives under /onboarding/v2/*) back
      // into v1 just because the legacy bootstrap signal says pending. v3
      // owns the user's journey while they're inside it.
      !location.pathname.startsWith("/onboarding/v2")
    ) {
      navigate("/onboarding", { replace: true })
      return
    }
    const intent = readPluginRestartIntent()
    const currentPath = `${location.pathname}${location.search}${location.hash}`
    if (!bootstrap.data.pending && intent && currentPath !== intent.path) {
      navigate(intent.path, { replace: true })
      return
    }
    if (bootstrap.data.pending || location.pathname !== "/onboarding") return
    const path = resolveOnboardingNavigation({
      returnTo: getReturnTo(),
      defaultRoute: bootstrap.data.defaultRoute,
    })
    clearReturnTo()
    // After product onboarding completes, divert into the starter-template flow
    // when an AuthIntent starter handoff is pending (else this is a no-op).
    navigate(resolveAuthStarterHandoffPath(path), { replace: true })
  })

  return (
    <Show
      when={!bootstrap.isPending || bootstrap.isError}
      fallback={<AppLoading message="Preparing your workspace..." scope="panel" />}
    >
      <Show
        when={!bootstrap.data?.pending || location.pathname === "/onboarding" || bootstrap.isError}
        fallback={<AppLoading message="Preparing your workspace..." scope="panel" />}
      >
        {props.children}
      </Show>
    </Show>
  )
}

export function AppInterface(props: {
  children?: JSX.Element
  defaultServer: ServerConnection.Key
  servers?: Array<ServerConnection.Any>
  router?: Component<BaseRouterProps>
  disableHealthCheck?: boolean
}) {
  return (
    <Dynamic component={props.router ?? Router}>
      <Route path="/auth/sign-in" component={AuthSignInAlias} />
      <Route
        path="/login"
        component={() => (
          <OnboardingV2Redirect>
            <GuestGuard>
              <Suspense fallback={<Loading />}>
                <LoginPage mode="signin" />
              </Suspense>
            </GuestGuard>
          </OnboardingV2Redirect>
        )}
      />
      <Route
        path="/signup"
        component={() => (
          <OnboardingV2Redirect>
            <GuestGuard>
              <Suspense fallback={<Loading />}>
                <LoginPage mode="signup" />
              </Suspense>
            </GuestGuard>
          </OnboardingV2Redirect>
        )}
      />
      <Route
        path="/callback"
        component={() => (
          <Suspense fallback={<Loading />}>
            <CallbackPage />
          </Suspense>
        )}
      />
      <Route
        path="/verify-email-pending"
        component={() => (
          <Suspense fallback={<Loading />}>
            <VerifyEmailPendingPage />
          </Suspense>
        )}
      />
      <Route
        path="/verify-email"
        component={() => (
          <Suspense fallback={<Loading />}>
            <VerifyEmailPage />
          </Suspense>
        )}
      />
      <Route
        path="/auth/verify-email"
        component={() => (
          <Suspense fallback={<Loading />}>
            <VerifyEmailPage />
          </Suspense>
        )}
      />
      <Route
        path="/auth/openanalyst/desktop-onboarding"
        component={() => (
          <Suspense fallback={<Loading />}>
            <OpenAnalystDesktopOnboardingPage />
          </Suspense>
        )}
      />
      <Route
        path="/onboarding/v2/:step?"
        component={() => (
          <Suspense fallback={<Loading />}>
            <OnboardingV2RouteWrapper
              defaultServer={props.defaultServer}
              servers={props.servers}
              disableHealthCheck={props.disableHealthCheck}
            />
          </Suspense>
        )}
      />

      <Route
        path="/runtime"
        component={() => (
          <AuthGuard>
            <Suspense fallback={<Loading />}>
              <OpenAnalystRuntimePage />
            </Suspense>
          </AuthGuard>
        )}
      />
      <Route
        component={(routeProps) => (
          <ProtectedLayout
            defaultServer={props.defaultServer}
            servers={props.servers}
            disableHealthCheck={props.disableHealthCheck}
            appChildren={props.children}
          >
            {routeProps.children}
          </ProtectedLayout>
        )}
      >
        <Route
          path="/"
          component={() => (
            <OnboardingV2Redirect>
              <Suspense fallback={<Loading />}>
                <HomeRoute />
              </Suspense>
            </OnboardingV2Redirect>
          )}
        />
        <Route
          path="/smartwork"
          component={() => (
            <Suspense fallback={<Loading />}>
              <SmartworkPage />
            </Suspense>
          )}
        />
        <Route
          path="/onboarding"
          component={() => (
            <Suspense fallback={<Loading />}>
              <OnboardingPage />
            </Suspense>
          )}
        />
        <Route
          path="/starter-handoff"
          component={() => (
            <Suspense fallback={<Loading />}>
              <StarterHandoffPage />
            </Suspense>
          )}
        />
        <Route path="/crm" component={CloudCrmSelectorPage} />
        <Route path="/crm/project/:projectId" component={CloudCrmProjectResolverPage} />
        <Route path="/crm/w/:crmWorkspaceId" component={CloudCrmWorkspacePage} />
        <Route
          path="/:dir"
          component={(routeProps) => (
            <DirectoryLayout>{routeProps.children}</DirectoryLayout>
          )}
        >
          <Route path="/" component={SessionIndexRoute} />
          <Route path="/session/:id?" component={SessionRoute} />
          <Route path="/mini-chat/:id?" component={MiniChatRoute} />
        </Route>
      </Route>
    </Dynamic>
  )
}
